
// // #################################################

import React, { useState, useRef } from "react";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Typography, CircularProgress } from "@mui/material";
import Card from "@mui/material/Card";
import EntitiesPopup from "layouts/components/EntitiesPopup"; // ✅ NEW

function AudienceGrowthRateChart({
  title,
  datasets,
  previousdatasets,
  futuredatasets = [],
  adjustedcurrentdatasets,
  adjustedpreviousdatasets,
  isPredictionEnabled = false,
  isLoading = false,
  entitlements, // ✅ Added prop
  viewMode, // ✅ NEW
  onViewModeChange, // ✅ NEW
  profileColorMap // ✅ Global Color Map
}) {
  // ✅ Popup state
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupDate, setPopupDate] = useState(null);

  const handlePointClick = (date) => {
    setPopupDate(date);
    setIsPopupOpen(true);
  };

  const handlePopupClose = () => {
    setIsPopupOpen(false);
    setPopupDate(null);
  };

  // ---------------------------------------------------------------------
  // RENDER LOADER
  // ---------------------------------------------------------------------
  const renderLoader = () => (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(255, 255, 255, 0.7)",
        zIndex: 10,
        borderRadius: "0.75rem",
      }}
    >
      <CircularProgress size={32} thickness={4} color="accent" />
    </MDBox>
  );

  const platformSet = new Set(datasets ? datasets.map((profile) => profile.platform) : []);
  let derivedTitle = "";

  if (platformSet.size === 1) {
    const platform = platformSet.values().next().value;
    derivedTitle =
      platform === "YouTube"
        ? "Subscribers Growth Rate Over Time"
        : "Followers Growth Rate Over Time";
  } else if (platformSet.size > 1) {
    const hasYouTube = [...platformSet].includes("YouTube");
    derivedTitle = hasYouTube
      ? "Subscribers Growth Rate Over Time"
      : "Followers Growth Rate Over Time";
  } else {
    derivedTitle = "Followers Growth Rate Over Time"; // Fallback
  }

  const safeDatasets = datasets || [];
  const safePreviousDatasets = previousdatasets || [];
  const safeFutureDatasets = futuredatasets || [];

  // ✅ Helper finders for adjusted datasets
  const findAdjustedCurrentProfile = (profileId) =>
    adjustedcurrentdatasets.find((p) => p.profileId === profileId);

  const findAdjustedPreviousProfile = (profileId) =>
    adjustedpreviousdatasets.find((p) => p.profileId === profileId);

  // ✅ Ensure proper alignment of predicted data with future dates
  const actualLabels = safeDatasets.length > 0 ? safeDatasets[0].data.map((item) => item.date) : [];
  const predictedLabels =
    isPredictionEnabled && safeFutureDatasets.length > 0
      ? safeFutureDatasets[0].data.slice(safeDatasets[0].data.length).map((item) => item.date)
      : [];
  const chartLabels = [...actualLabels, ...predictedLabels];

  const updatedPreviousDatasets = isPredictionEnabled
    ? [...safePreviousDatasets, ...safeDatasets]
    : safePreviousDatasets;

  /**
   * ✅ Enhanced Function to calculate follower/subscriber growth rate percentage.
   * Formula: ((Current Followers - Previous Followers) / Previous Followers) * 100
   * Handles actual vs predicted logic for both current and previous points.
   */
  const calculateGrowthRate = (profile, adjustedFinder) => {
    const adjustedProfile = adjustedFinder(profile.profileId);
    const isYouTube = profile.platform === "YouTube";
    const actualKey = isYouTube ? "subscribers" : "followers";
    const predictedKey = isYouTube ? "predicted_subscribers" : "predicted_followers";

    const growthRates = [];
    const isPredictedFlags = [];

    profile.data.forEach((item, index) => {
      // ✅ Prefer actual if available, else predicted
      const currentFollowers =
        item[actualKey] && item[actualKey] > 0
          ? item[actualKey]
          : item[predictedKey] ?? null;

      const isPredicted = !(item[actualKey] && item[actualKey] > 0);
      isPredictedFlags.push(isPredicted);

      // ✅ Determine previous point
      let previousFollowers;
      if (index === 0 && adjustedProfile) {
        const prevItem = adjustedProfile.data[0] || {};
        previousFollowers =
          prevItem[actualKey] && prevItem[actualKey] > 0
            ? prevItem[actualKey]
            : prevItem[predictedKey] ?? null;
      } else if (index > 0) {
        const prevItem = profile.data[index - 1] || {};
        previousFollowers =
          prevItem[actualKey] && prevItem[actualKey] > 0
            ? prevItem[actualKey]
            : prevItem[predictedKey] ?? null;
      }

      const growthRate =
        previousFollowers && previousFollowers > 0
          ? ((currentFollowers - previousFollowers) / previousFollowers) * 100
          : null;

      growthRates.push(growthRate);
    });

    return { growthRates, isPredictedFlags };
  };

  /**
   * ✅ Simpler Function for Previous Datasets (no predicted logic)
   */
  const calculatePreviousGrowthRate = (profile, adjustedFinder) => {
    const adjustedProfile = adjustedFinder(profile.profileId);
    const metricKey = profile.platform === "YouTube" ? "subscribers" : "followers";

    return profile.data.map((item, index) => {
      const currentFollowers = item[metricKey] ?? null;
      if (currentFollowers === null) return null;

      let previousFollowers;
      if (index === 0 && adjustedProfile) {
        previousFollowers = adjustedProfile.data[0]?.[metricKey] ?? null;
      } else if (index > 0) {
        previousFollowers = profile.data[index - 1]?.[metricKey] ?? null;
      } else {
        previousFollowers = null;
      }

      return previousFollowers && previousFollowers > 0
        ? ((currentFollowers - previousFollowers) / previousFollowers) * 100
        : null;
    });
  };

  // ✅ Construct chart data with prediction flags included
  const computedChartData = {
    labels: chartLabels,

    datasets: [
      // 🔹 Actual Growth Rate (adjustedCurrent)
      ...safeDatasets.map((profile) => {
        const { growthRates, isPredictedFlags } = calculateGrowthRate(
          profile,
          findAdjustedCurrentProfile
        );
        return {
          label: profile.name || profile.label,
          data: growthRates,
          isPredictedFlags,
          profileId: profile.profileId,
          profilePictureUrl: profile.profilePictureUrl || "",
          platform: profile.platform || "Unknown",
          metricName:
            profile.platform === "YouTube"
              ? "Subscribers Growth Rate (%)"
              : profile.platform === "Facebook" || profile.platform === "Instagram"
                ? "Followers Growth Rate (%)"
                : "Metric Growth Rate (%)",
        };
      }),

      // 🔹 Predicted Growth Rate Data (future of current)
      ...(isPredictionEnabled
        ? safeFutureDatasets.map((profile) => {
          const { growthRates, isPredictedFlags } = calculateGrowthRate(
            profile,
            findAdjustedCurrentProfile
          );
          return {
            label: `${profile.name || profile.label} (Predicted)`,
            data: growthRates,
            isPredictedFlags,
            borderDash: [5, 5],
            profileId: profile.profileId,
            profilePictureUrl: profile.profilePictureUrl || "",
            platform: profile.platform || "Unknown",
            metricName:
              profile.platform === "YouTube"
                ? "Predicted Subscribers Growth Rate (%)"
                : profile.platform === "Facebook" || profile.platform === "Instagram"
                  ? "Predicted Followers Growth Rate (%)"
                  : "Predicted Metric Growth Rate (%)",
          };
        })
        : []),
    ],

    // 🔹 Previous Growth Rate Data (adjustedPrevious, no predicted logic)
    previousdatasets: updatedPreviousDatasets.map((profile) => ({
      label: profile.name || profile.label,
      data: calculatePreviousGrowthRate(profile, findAdjustedPreviousProfile),
      profileId: profile.profileId,
      profilePictureUrl: profile.profilePictureUrl || "",
      platform: profile.platform || "Unknown",
      metricName:
        profile.platform === "YouTube"
          ? "Subscribers Growth Rate (%)"
          : profile.platform === "Facebook" || profile.platform === "Instagram"
            ? "Followers Growth Rate (%)"
            : "Metric Growth Rate (%)",
    })),
  };

  // -----------------------------------------------------------
  // PERSISTENCE LOGIC
  // -----------------------------------------------------------
  // We use a Ref to store the last valid data to show during loading
  const lastChartDataRef = useRef(null);

  // If we have actual data, update the ref
  if (safeDatasets.length > 0) {
    lastChartDataRef.current = computedChartData;
  }

  let dataToRender = computedChartData;
  if (isLoading && safeDatasets.length === 0 && lastChartDataRef.current) {
    dataToRender = lastChartDataRef.current;
  }

  const hasData = (dataToRender.datasets && dataToRender.datasets.length > 0);

  // 📘 Tooltip content remains unchanged
  const tooltipContent = (
    <div style={{ maxWidth: "350px", textAlign: "left" }}>
      <Typography variant="body2" gutterBottom>
        <strong>Description:</strong>
      </Typography>
      <Typography variant="caption" display="block" gutterBottom>
        This chart shows the percentage rate at which a profile’s audience is growing over time.
        Instead of just showing the absolute number of new audience, it calculates the growth rate
        relative to the total audience, providing a better way to compare profiles of different
        sizes.
      </Typography>

      <Typography variant="body2" gutterBottom>
        <strong>How to read the chart:</strong>
      </Typography>
      <Typography variant="caption" display="block" gutterBottom>
        - Higher values indicate a faster audience growth rate. <br />
        - Lower or negative values indicate slow or declining audience growth. <br />
        - Spikes may suggest viral content, successful campaigns, or trending activity.
      </Typography>

      <Typography variant="body2" gutterBottom>
        <strong>Insights you can gain:</strong>
      </Typography>
      <Typography variant="caption" display="block">
        - Understand how quickly a profile is gaining audience. <br />
        - Compare growth trends across different platforms or profiles. <br />
        - Identify content or campaigns that drive significant audience growth.
      </Typography>
    </div>
  );

  return (
    <div style={{ position: "relative" }}>
      {isLoading && renderLoader()}

      {hasData ? (
        <DefaultLineChart
          title={derivedTitle}
          description={tooltipContent}
          height="19.125rem"
          chart={dataToRender}
          aggregate={true}
          rate={true}
          onPointClick={handlePointClick} // ✅ Pass click handler
          is_derived={true}
          rawData={safeDatasets} // ✅ Pass rich data (posts/videos) to AI
          entitlements={entitlements} // ✅ Pass entitlements
          viewMode={viewMode} // ✅ Pass down CORRECT PROP NAME
          onViewModeChange={onViewModeChange} // ✅ Pass down
          profileColorMap={profileColorMap} // ✅ Pass Global Colors
        />
      ) : (
        <Card sx={{ height: "19.125rem", display: "flex", justifyContent: "center", alignItems: "center" }}>
          {!isLoading && (
            <MDTypography variant="body2" color="text">
              No follower growth rate data available.
            </MDTypography>
          )}
        </Card>
      )}

      {isPopupOpen && (
        <EntitiesPopup
          isModalOpen={isPopupOpen}
          onClose={handlePopupClose}
          datasets={datasets}
          date={popupDate}
        />
      )}
    </div>
  );
}

// 🛡️ PropTypes Validation
AudienceGrowthRateChart.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  datasets: PropTypes.array.isRequired,
  previousdatasets: PropTypes.array.isRequired,
  futuredatasets: PropTypes.array,
  adjustedcurrentdatasets: PropTypes.array.isRequired,
  adjustedpreviousdatasets: PropTypes.array.isRequired,
  isPredictionEnabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  entitlements: PropTypes.object,
  viewMode: PropTypes.string,
  onViewModeChange: PropTypes.func,
  profileColorMap: PropTypes.object,
};

export default AudienceGrowthRateChart;